import { toast } from "sonner";
import { ExpandableCopyDetails } from "./expandable-copy-details";
import {
  formatErrorForClipboard,
  getPrimaryErrorMessage,
  type FormatErrorForCopyOptions,
} from "@/lib/format-error-for-copy";

/**
 * Shows an error toast whose full message copies to the clipboard when clicked.
 * Clipboard includes the error message, JS stack trace when present, HTTP response body,
 * and AI input/output context when provided.
 * Uses `toast.custom` so the control is real DOM (Sonner ignores React elements passed
 * as the sole `toast.error` title when `jsx` is unset).
 */
export function toastCopyableError(
  error: unknown,
  fallback: string,
  options?: FormatErrorForCopyOptions
): void {
  const displayMessage = getPrimaryErrorMessage(error, fallback);
  const copyPayload = formatErrorForClipboard(error, fallback, options);
  toast.custom(
    () => (
      <div className="flex w-full max-w-[356px] gap-3 rounded-lg border border-border bg-background p-4 text-foreground shadow-lg">
        <div className="text-destructive pt-0.5" aria-hidden>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5 shrink-0"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1 text-left text-sm">
          <span className="block whitespace-pre-wrap break-words">{displayMessage}</span>
          <ExpandableCopyDetails
            copyText={copyPayload}
            aiInputText={options?.aiInputText}
            aiOutputText={options?.aiOutputText}
            copyLabel="Click to copy"
          />
        </div>
      </div>
    ),
    { duration: 15000 }
  );
}
